import { ExternalLink } from "lucide-react"
import type { FC } from "react"
import { Link, useLocation } from "react-router"
import { Logo } from "@/components/Logo"
import { buildConsumerRestaurantUrl } from "@/lib/consumer-links"
import { useTenantAccess } from "@/lib/tenant-access"
import { useTenantShop } from "@/lib/use-tenant-shop"
import { cn } from "@/lib/utils"
import { TenantMenu } from "./tenant-menu"

const NAV_ITEMS = [
  {
    label: "ダッシュボード",
    to: "/dashboard",
    requiresManager: false,
  },
  {
    label: "チケット",
    to: "/tickets",
    requiresManager: true,
  },
  {
    label: "売上",
    to: "/sales",
    requiresManager: true,
  },
  {
    label: "店舗設定",
    to: "/setting/shop",
    requiresManager: true,
  },
] as const

export const TenantHeader: FC = () => {
  const { canManageTenant } = useTenantAccess()
  const { shopSettings } = useTenantShop()
  const location = useLocation()
  const pathname = location.pathname
  const shopUrl = shopSettings?.restaurantId
    ? buildConsumerRestaurantUrl(import.meta.env.VITE_CONSUMER_BASE_URL, shopSettings.restaurantId)
    : null

  const navItems = NAV_ITEMS.filter((item) => !item.requiresManager || canManageTenant).map(
    (item) => ({
      ...item,
      isActive:
        item.to === "/dashboard"
          ? pathname.startsWith("/dashboard")
          : item.to === "/tickets"
            ? pathname.startsWith("/tickets")
            : item.to === "/sales"
              ? pathname.startsWith("/sales")
              : pathname.startsWith("/setting"),
    })
  )

  const mobileTitle = (() => {
    if (pathname.startsWith("/tickets")) return "チケット"
    if (pathname.startsWith("/sales")) return "売上"
    if (pathname.startsWith("/setting")) return "店舗設定"
    return "ダッシュボード"
  })()

  return (
    <header className="w-full border-b border-border bg-bg-white">
      <div className="relative flex h-12 items-center justify-between px-4 sm:px-6 lg:px-0">
        <Link to="/dashboard" className="flex min-w-0 items-center lg:pl-6">
          <Logo className="h-[21px] w-[160px] lg:h-[30px] lg:w-[233px]" />
        </Link>
        <nav className="absolute left-1/2 top-0 hidden h-full -translate-x-1/2 items-center lg:flex">
          {navItems.map((item) => (
            <Link
              key={item.to}
              to={item.to}
              aria-current={item.isActive ? "page" : undefined}
              className={cn(
                "flex h-full w-[165px] items-center justify-center px-4 text-[15px] font-semibold leading-[1.5] text-foreground transition-colors",
                item.isActive ? "bg-bg-secondary text-secondary" : "hover:bg-muted/50"
              )}
            >
              {item.label}
            </Link>
          ))}
        </nav>
        <div className="flex items-center justify-end gap-1.5 lg:pr-2">
          {shopUrl ? (
            <a
              href={shopUrl}
              target="_blank"
              rel="noreferrer"
              className="hidden h-7 w-[343px] items-center justify-end gap-0.5 px-2 text-[13px] font-semibold leading-[1.35] text-foreground transition-colors hover:bg-muted/40 lg:inline-flex"
            >店舗ページをみる<ExternalLink className="h-4 w-4" strokeWidth={2.25} />
            </a>
          ) : null}
          <span className="max-w-[140px] truncate text-[11px] font-semibold text-foreground lg:hidden">
            {mobileTitle}
          </span>
          <TenantMenu
            triggerClassName="size-9 rounded-full border-none bg-white/80 shadow-none backdrop-blur-[10px] hover:bg-white/90"
            iconClassName="h-5 w-5"
          />
        </div>
      </div>
    </header>
  )
}
